import React, { useEffect, useRef, useState } from 'react';
import {
    SearchField,
    RowList,
    Row,
    Divider,
    IconFunnelRegular,
    IconSearchRegular,
    skinVars,
    Text2,
    DateField,
    Chip,
    EmptyState,
    IconInvoiceEuroRegular,
    Spinner,
} from '@telefonica/mistica';
import './index.scss';
import { getFormatDate } from '../../../../../utils/dataTransformation/dateConversions';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { getClaimList } from '../../../../../services/BillService';
import { IClaimList } from '../../../../../models/Bills.interfaces';
import HeaderComponent from '../../../../../components/HeaderComponent';
import { setTypeTagstatus } from '../../../utils/functions';
import { useBillsContext } from '../../../../../context/bills-context/BillsContext';
import InfiniteScroll from 'react-infinite-scroll-component';

const ClaimList: React.FC = () => {
    const [claimList, setClaimList] = useState<IClaimList[]>([]);
    const navigate = useNavigate();
    const [searchActive, setSearchActive] = useState(false);
    const [filterActive, setFilterActive] = useState(false);
    const [chipSearchActive, setChipSearchActive] = useState(false);
    const [chipSearchNoActive, setChipSearchNoActive] = useState(true);
    const [chipFilter, setChipFilter] = useState(true);
    const [chipFilterClosable, setChipFilterClosable] = useState(false);
    const [chipFilterActive, setChipFilterActive] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const today = new Date();
    const sixMonthsAgo = new Date(today);
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    const [startDate, setStartDate] = useState<Date | null>(sixMonthsAgo);
    const [endDate, setEndDate] = useState<Date | null>(today);
    const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
    const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
    const { setClaimId } = useBillsContext();
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const initialFetchDone = useRef(false);
    const [newDateSearch, setNewDateSearch] = useState(false);
    const [firstLoading, setfirstLoading] = useState(true);

    const navigationBackHandler = () => navigate('/bills');

    useEffect(() => {
        const fetchInitial = async () => {
            await claimListRequest(true);
            initialFetchDone.current = true;
            setfirstLoading(false);
        };
        fetchInitial();
    }, []);

    useEffect(() => {
        if (newDateSearch) {
            setNewDateSearch(false);
            (async () => {
                await claimListRequest(true);
            })();
        }
    }, [newDateSearch]);

    const claimListRequest = async (isInitial = false) => {
        if (!isInitial && !initialFetchDone.current) return;

        let startDateStr, endDateStr;
        if (filterActive) {
            if (!filterStartDate || !filterEndDate) return;
            startDateStr = filterStartDate.toISOString();
            endDateStr = filterEndDate.toISOString();
        } else {
            startDateStr = startDate!.toISOString();
            endDateStr = endDate!.toISOString();
        }

        try {
            const response = await getClaimList(page, 10, startDateStr, endDateStr);
            setfirstLoading(false);
            setClaimList((prevState) => [...prevState, ...response.data.economicCustomerProblems]);
            setPage((prevState) => prevState + 1);
            setHasMore(response.data._meta.moreElements);
        } catch (error) {
            setfirstLoading(false);
            console.error(error);
        }
    };

    const handleSearchActiveChange = () => {
        setSearchActive(!searchActive);
        setChipSearchActive(!chipSearchActive);
        setChipSearchNoActive(!chipSearchNoActive);
        setChipFilterClosable(false);
        setChipFilterActive(false);
        setChipFilter(true);
        setFilterActive(false);
    };

    const handleFilterActive = () => {
        const nextFilterstate = !filterActive;
        setFilterActive(nextFilterstate);
        setChipFilterClosable(nextFilterstate);
        setChipFilter(!nextFilterstate);
        setChipFilterActive(false);
        setSearchActive(false);
        setChipSearchNoActive(true);
        setChipSearchActive(false);
        if (!nextFilterstate) {
            setFilterStartDate(null);
            setFilterEndDate(null);
            if (filterStartDate && filterEndDate !== null) {
                setfirstLoading(true);
                window.location.reload();
            }
        }
    };

    const handleDateChange =
        (setter: (arg0: Date | null) => void, otherDate: Date | null) =>
        (event: { target: { value: string | number | Date } }) => {
            const selectedDate = event.target.value ? new Date(event.target.value) : null;
            setter(selectedDate);

            if (
                (setter === setFilterStartDate ? selectedDate : filterStartDate) &&
                (setter === setFilterEndDate ? selectedDate : filterEndDate)
            ) {
                setClaimList([]);
                setPage(1);
                setNewDateSearch(true);
            }
        };

    const filteredClaims = (
        (claimList?.filter((claim: IClaimList) => {
            const matchesSearch = claim.code.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesSearch;
        }) as IClaimList[]) || []
    ).sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime());

    const claimClickHandler = (claimId: string) => {
        setClaimId(claimId);
        navigate(`/claims/claim-detail`);
    };

    if (firstLoading) {
        return (
            <div className='c-spinner'>
                <Spinner size={24} />
                <span style={{ background: 'white' }} className='c-spinner-overlay'></span>
            </div>
        );
    }

    return (
        <div className='bills-claimList-page'>
            <HeaderComponent
                title={t('billsClaimsHistory')}
                navigationBarOnBack={navigationBackHandler}
                breadcrumbs={[
                    {
                        url: '/bills',
                        title: t('invoice'),
                    },
                ]}
            />

            <div className='bills-claimList-page__container'>
                <div className='bills-claimList-page__inputContainer'>
                    <div className='bills-claimList-page__inputContainer-chips'>
                        <div>
                            {chipSearchActive && (
                                <Chip
                                    Icon={IconSearchRegular}
                                    onPress={() => {
                                        handleSearchActiveChange();
                                    }}
                                    active={true}
                                >
                                    {t('billsSearchInput')}
                                </Chip>
                            )}
                            {chipSearchNoActive && (
                                <Chip
                                    Icon={IconSearchRegular}
                                    onPress={() => {
                                        handleSearchActiveChange();
                                    }}
                                >
                                    {t('billsSearchInput')}
                                </Chip>
                            )}
                        </div>
                        <div>
                            {chipFilter && (
                                <Chip
                                    Icon={IconFunnelRegular}
                                    onPress={() => {
                                        handleFilterActive();
                                    }}
                                >
                                    {t('billsFilterInput')}
                                </Chip>
                            )}
                            {chipFilterActive && (
                                <Chip
                                    Icon={IconFunnelRegular}
                                    onPress={() => {
                                        handleFilterActive();
                                    }}
                                    badge={filterActive && (startDate || endDate) ? filteredClaims.length : undefined}
                                    active={true}
                                >
                                    {t('billsFilterInput')}
                                </Chip>
                            )}
                            {chipFilterClosable && (
                                <Chip
                                    Icon={IconFunnelRegular}
                                    onClose={() => {
                                        handleFilterActive();
                                    }}
                                >
                                    {t('billsFilterInput')}
                                </Chip>
                            )}
                        </div>
                    </div>
                    <div className='bills-claimList-page__inputContainer-inputs'>
                        {searchActive && (
                            <div className='bills-claimList-page__inputContainer-searchContainer'>
                                <SearchField
                                    name='search'
                                    autoComplete='off'
                                    dataAttributes={{ testid: 'search-field' }}
                                    label={t('billsSearchInputPlaceHolder')}
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    fullWidth={true}
                                />
                            </div>
                        )}
                        {filterActive && (
                            <div className='bills-claimList-page__inputContainer-filterContainer'>
                                <div>
                                    <DateField
                                        name='date'
                                        autoComplete='off'
                                        dataAttributes={{ testid: 'start-date-field' }}
                                        label={t('billsFilterInputStartDate')}
                                        onChange={handleDateChange(setFilterStartDate, filterEndDate)}
                                        fullWidth={true}
                                        value={filterStartDate ? filterStartDate.toISOString().slice(0, 10) : ''}
                                        max={filterEndDate ? filterEndDate : undefined}
                                    />
                                </div>
                                <div>
                                    <DateField
                                        name='date'
                                        autoComplete='off'
                                        dataAttributes={{ testid: 'end-date-field' }}
                                        label={t('billsSearchInputEndDate')}
                                        onChange={handleDateChange(setFilterEndDate, filterStartDate)}
                                        fullWidth={true}
                                        value={filterEndDate ? filterEndDate.toISOString().slice(0, 10) : ''}
                                        min={filterStartDate ? filterStartDate : undefined}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {filteredClaims.length > 0 && (
                    <div className='bills-claimList-page__list'>
                        <InfiniteScroll
                            dataLength={claimList.length}
                            next={claimListRequest}
                            hasMore={hasMore}
                            loader={<h4>{t('loading')}</h4>}
                            scrollThreshold={0.9}
                        >
                            <RowList>
                                {filteredClaims.map((claim: IClaimList) => {
                                    return (
                                        <Row
                                            key={claim.id}
                                            headline={setTypeTagstatus(claim.phase)}
                                            title={`${t('billsClaimInvoice')} ${getFormatDate(
                                                claim.creationDate,
                                                'DD/MM/YYYY',
                                            )}`}
                                            description={claim.code}
                                            withChevron={false}
                                            extra={
                                                <div className='bills-claimList-page__list-billMoreInfo'>
                                                    <Text2 regular color={skinVars.colors.textLink}>
                                                        {t('billsMoreInformation')}
                                                    </Text2>
                                                </div>
                                            }
                                            onPress={() => claimClickHandler(claim.id)}
                                        />
                                    );
                                })}
                                <div className='bills-claimList-page__list-divider'>
                                    <Divider />
                                </div>
                            </RowList>
                        </InfiniteScroll>
                    </div>
                )}
                {filteredClaims.length <= 0 && (
                    <div className='bills-claimList-page__container-emptyState'>
                        <EmptyState
                            title={t('noClaimsTitle')}
                            description={t('noClaimsDescription')}
                            asset={<IconInvoiceEuroRegular color={skinVars.colors.brand} size={64} />}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClaimList;
